import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Box } from '@mui/material';

import { usePhoneScreen } from '@/designs-dev/device/PhoneScreenContext';
import { useHideTabBar } from '@/designs-dev/device/TabBarVisibility';

const SETTLE_MS = 260;

// A full-screen overlay that cross-fades in over the current screen — the app
// dissolving into Keni, rather than a push or a slide. Same pattern as
// ModalScreen/PushScreen but the transition is opacity. Children get `close()`,
// which fades out then calls `onClose`.
const FadeScreen = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(true), []);
  useHideTabBar();

  const closing = useRef(false);
  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    window.setTimeout(onClose, SETTLE_MS + 40);
  };

  const screenEl = usePhoneScreen();

  const page = (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.default',
        zIndex: 18,
        opacity: open ? 1 : 0,
        transition: `opacity ${SETTLE_MS}ms ease`,
      }}
    >
      {children(close)}
    </Box>
  );

  return screenEl ? createPortal(page, screenEl) : page;
};

export default FadeScreen;
